import { t } from "i18next";
import { Controller, useForm } from "react-hook-form";
import { useSalutationOptions, useTitleOptions } from "~/ScanDMC/components/ContactFormOverlay/dmcContactFormUtils";
import { Button } from "~/common/components/Button/Button";
import { CheckboxWithLabel } from "~/common/components/CheckboxWithLabel";
import Modal from "~/common/components/Modal";
import { SimpleSelect } from "~/common/components/Select/SimpleSelect";
import { MaterialInput } from "~/common/components/materialInput/MaterialInput";
import { type UserInfoData } from "../types";
import { SimpleAutocomplete } from "~/common/components/Select/SimpleAutocompleteSelect";
import { useCountryOptions } from "../utils";
import { useIsMinDesktopSize, useIsMinTabletSize } from "~/common/hooks/useMediaQuery";
import classNames from "classnames";
import classes from "./UserInfoModal.module.less";
import { PHONE_REGEXP, ZIPCODE_REGEXP } from "~/utils/validations";
import { Stacker } from "~/common/components/Stacker/Stacker";

interface UserInfoModalProps {
  closeModal: () => void;
  formSubmit: (data: UserInfoData) => void;
  values: UserInfoData;
}

const UserInfoModal = ({ closeModal, formSubmit, values }: UserInfoModalProps) => {
  const { control, handleSubmit } = useForm({ defaultValues: { ...values } });

  const titleOptions = useTitleOptions();
  const salutationOptions = useSalutationOptions();
  const countryOptions = useCountryOptions();

  const isDesktop = useIsMinDesktopSize();
  const isTablet = useIsMinTabletSize();

  return (
    <Modal
      onClose={closeModal}
      title={t("request.userInformation")}
      footerChildren={
        <div className={`d-flex ${isTablet ? "flex-justify-between" : "flex-justify-end"} flex-align-center w-100`}>
          {isTablet && <div>{t("mandatory")}</div>}
          <Stacker direction={"row"} gap={8}>
            <Button size={"medium"} onClick={closeModal} variant="text">
              {t("bulkUpload.buttons.cancel")}
            </Button>
            <Button size={"medium"} onClick={() => handleSubmit(formSubmit)()}>
              {t("editWishlistNamePopup.buttons.edit.ok")}
            </Button>
          </Stacker>
        </div>
      }
    >
      <form className={classes.formContainer}>
        <div className={classes.fill}>
          <Controller
            name={"company"}
            control={control}
            rules={{ required: t("page.scandmc.form.error.required") }}
            render={({ field: { ref, ...inputProps }, fieldState: { error } }) => (
              <MaterialInput
                className={classes.big}
                label={t("page.completeRegistration.companyName") + "*"}
                error={!!error}
                inputRef={ref}
                {...inputProps}
                helperLabel={error?.message ?? null}
              />
            )}
          />
        </div>

        <div className={classes.fill}>
          <Controller
            name={"titleCode"}
            control={control}
            render={({ field: { ref, ...inputProps }, fieldState: { error } }) => (
              <SimpleSelect
                inputRef={ref}
                fullWidth
                values={titleOptions}
                label={t("page.scandmc.form.title")}
                error={!!error}
                {...inputProps}
              />
            )}
          />
        </div>

        <div className={classes.fill}>
          <Controller
            name={"salutationCode"}
            control={control}
            rules={{ required: t("page.scandmc.form.error.required") }}
            render={({ field: { ref, ...inputProps }, fieldState: { error } }) => (
              <SimpleSelect
                inputRef={ref}
                fullWidth
                values={salutationOptions}
                label={t("page.scandmc.form.salutation") + "*"}
                error={!!error}
                helperText={error?.message ?? null}
                {...inputProps}
              />
            )}
          />
        </div>

        <div className={classes.fill}>
          <Controller
            name={"firstName"}
            control={control}
            rules={{ required: t("page.scandmc.form.error.required") }}
            render={({ field: { ref, ...inputProps }, fieldState: { error } }) => (
              <MaterialInput
                label={t("page.scandmc.form.firstName") + "*"}
                error={!!error}
                inputRef={ref}
                {...inputProps}
                helperLabel={error?.message ?? null}
              />
            )}
          />
        </div>

        <div className={classes.fill}>
          <Controller
            name={"lastName"}
            control={control}
            rules={{ required: t("page.scandmc.form.error.required") }}
            render={({ field: { ref, ...inputProps }, fieldState: { error } }) => (
              <MaterialInput
                label={t("page.scandmc.form.lastName") + "*"}
                error={!!error}
                inputRef={ref}
                {...inputProps}
                helperLabel={error?.message ?? null}
              />
            )}
          />
        </div>

        <div className={classes.fill}>
          <Controller
            name={"line1"}
            control={control}
            render={({ field: { ref, ...inputProps }, fieldState: { error } }) => (
              <MaterialInput
                label={t("page.completeRegistration.address")}
                error={!!error}
                inputRef={ref}
                {...inputProps}
                helperLabel={error?.message ?? null}
              />
            )}
          />
        </div>

        <div className={classes.fill}>
          <Controller
            name={"additionalLine1"}
            control={control}
            render={({ field: { ref, ...inputProps }, fieldState: { error } }) => (
              <MaterialInput
                label={t("page.completeRegistration.addressAdditional")}
                error={!!error}
                inputRef={ref}
                {...inputProps}
                helperLabel={error?.message ?? null}
              />
            )}
          />
        </div>

        <div className={!isTablet && classes.fill}>
          <Controller
            name={"zipCode"}
            control={control}
            render={({ field: { ref, ...inputProps }, fieldState: { error } }) => (
              <MaterialInput
                label={t("page.completeRegistration.zipCode")}
                error={!!error}
                inputRef={ref}
                onBeforeInput={(e) => {
                  // @ts-ignore
                  const val = e.target.value + e.data;
                  if (!ZIPCODE_REGEXP.test(val.trim())) {
                    e.preventDefault();
                    e.stopPropagation();
                  }
                }}
                {...inputProps}
                helperLabel={error?.message ?? null}
              />
            )}
          />
        </div>

        <div className={!isTablet && classes.fill}>
          <Controller
            name={"city"}
            control={control}
            render={({ field: { ref, ...inputProps }, fieldState: { error } }) => (
              <MaterialInput
                label={t("page.completeRegistration.city")}
                error={!!error}
                inputRef={ref}
                {...inputProps}
                helperLabel={error?.message ?? null}
              />
            )}
          />
        </div>

        <div className={classes.fill}>
          <Controller
            name={"country"}
            control={control}
            rules={{ required: t("page.scandmc.form.error.required") }}
            render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => (
              <SimpleAutocomplete
                label={t("page.completeRegistration.country") + "*"}
                values={countryOptions}
                fullWidth
                error={!!error}
                onBlur={onBlur}
                value={value}
                onChange={onChange}
              />
            )}
          />
        </div>

        <div className={classes.fill}>
          <Controller
            name={"phone"}
            control={control}
            render={({ field: { ref, ...inputProps }, fieldState: { error } }) => (
              <MaterialInput
                label={t("page.completeRegistration.phone")}
                error={!!error}
                inputRef={ref}
                {...inputProps}
                helperLabel={error?.message ?? null}
                onBeforeInput={(e) => {
                  // @ts-ignore
                  const val = e.target.value + e.data;
                  if (!PHONE_REGEXP.test(val.trim())) {
                    e.preventDefault();
                    e.stopPropagation();
                  }
                }}
              />
            )}
          />
        </div>

        <div className={classNames(classes.email, classes.fill)}>
          <label className="label-large">{t("myCompany.form.email")}</label>
          <span className="title-medium">{window.SCHA.user.email}</span>
        </div>

        <div className={classNames(classes.fill, classes.checkBox)}>
          <Controller
            name={"updateUser"}
            control={control}
            render={({ field: { value, ref, ...inputProps } }) => (
              <CheckboxWithLabel checked={value} inputRef={ref} {...inputProps}>
                {t("request.saveUserInfo")}
              </CheckboxWithLabel>
            )}
          />
        </div>
      </form>
    </Modal>
  );
};

export default UserInfoModal;
